import { useState } from 'react'
import { AlertCircle, CheckCircle, Loader2, XCircle, ExternalLink, Undo2 } from 'lucide-react'
import { useBookmarkStore } from '../store/bookmarkStore'
import { Button } from './Button'

interface BookmarkValidatorProps {
  onClose: () => void
  bookmarkIds?: string[] // 如果提供，则只检查指定的书签
}

interface ValidationResult {
  id: string
  title: string
  url: string
  status: 'pending' | 'valid' | 'invalid' | 'error'
  statusCode?: number
  error?: string
  originalStatus?: 'pending' | 'valid' | 'invalid' | 'error'
  originalStatusCode?: number
  originalError?: string
}

export function BookmarkValidator({ onClose, bookmarkIds }: BookmarkValidatorProps) {
  const { bookmarks, updateBookmark } = useBookmarkStore()
  const [results, setResults] = useState<ValidationResult[]>([])
  const [isChecking, setIsChecking] = useState(false)

  // 检查单个URL的有效性
  const checkUrl = async (url: string): Promise<{ isValid: boolean; statusCode?: number; error?: string }> => {
    try {
      const response = await fetch('/api/check-url', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ url }),
      })
      
      // 添加对空响应的处理
      if (!response.ok) {
        return {
          isValid: false,
          statusCode: response.status,
          error: `HTTP 状态码: ${response.status}`,
        };
      }
      
      // 安全地解析JSON
      let data;
      try {
        const text = await response.text();
        data = text ? JSON.parse(text) : {};
      } catch (parseError) {
        return {
          isValid: false,
          error: '解析响应失败: ' + (parseError instanceof Error ? parseError.message : String(parseError)),
        };
      }
      
      return {
        isValid: data.isValid,
        statusCode: data.statusCode,
        error: data.error,
      }
    } catch (error) {
      return {
        isValid: false,
        error: error instanceof Error ? error.message : '未知错误',
      }
    }
  }

  // 开始检查书签
  const startValidation = async () => {
    setIsChecking(true)
    const targetBookmarks = bookmarkIds
      ? bookmarks.filter(b => bookmarkIds.includes(b.id))
      : bookmarks

    // 初始化结果
    setResults(
      targetBookmarks.map(b => ({
        id: b.id,
        title: b.title,
        url: b.url,
        status: 'pending',
      }))
    )

    // 并行检查所有URL，但限制并发数
    const batchSize = 5
    for (let i = 0; i < targetBookmarks.length; i += batchSize) {
      const batch = targetBookmarks.slice(i, i + batchSize)
      await Promise.all(
        batch.map(async (bookmark) => {
          const check = await checkUrl(bookmark.url)
          setResults(prev =>
            prev.map(r =>
              r.id === bookmark.id
                ? {
                    ...r,
                    status: check.isValid ? 'valid' : check.error ? 'error' : 'invalid',
                    statusCode: check.statusCode,
                    error: check.error,
                  }
                : r
            )
          )
        })
      )
    }

    setIsChecking(false)
  }

  // 手动标记书签为有效
  const markAsValid = (id: string) => {
    // 保存原始状态以便撤销
    const originalResult = results.find(r => r.id === id);
    
    setResults(prev =>
      prev.map(r =>
        r.id === id
          ? {
              ...r,
              status: 'valid',
              error: undefined,
              statusCode: undefined, // 清除状态码
              originalStatus: r.status, // 保存原始状态
              originalError: r.error,
              originalStatusCode: r.statusCode,
            }
          : r
      )
    )
  }
  
  // 撤销手动标记为有效
  const undoMarkAsValid = (id: string) => {
    setResults(prev =>
      prev.map(r =>
        r.id === id
          ? {
              ...r,
              status: r.originalStatus || 'invalid',
              error: r.originalError,
              statusCode: r.originalStatusCode,
              originalStatus: undefined,
              originalError: undefined,
              originalStatusCode: undefined,
            }
          : r
      )
    )
  }

  // 标记无效书签
  const markInvalidBookmarks = () => {
    results
      .filter(r => (r.status === 'invalid' || r.status === 'error') && r.originalStatus === undefined)
      .forEach(r => {
        updateBookmark(r.id, {
          tags: ['无效链接'],
        })
      })
    onClose()
  }

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4">
      <div className="bg-white rounded-lg p-6 max-w-2xl w-full max-h-[80vh] flex flex-col">
        <div className="flex justify-between items-center mb-4">
          <h2 className="text-lg font-medium">
            检查书签有效性
            {bookmarkIds && `（${bookmarkIds.length} 个选中的书签）`}
          </h2>
          <button
            onClick={onClose}
            className="text-gray-500 hover:text-gray-700"
          >
            ×
          </button>
        </div>

        <div className="flex space-x-2 mb-4">
          <Button
            onClick={startValidation}
            disabled={isChecking}
            className="flex items-center"
          >
            {isChecking && <Loader2 className="w-4 h-4 mr-2 animate-spin" />}
            开始检查
          </Button>
          {results.length > 0 && (
            <Button
              variant="outline"
              onClick={markInvalidBookmarks}
              disabled={isChecking}
            >
              标记无效书签
            </Button>
          )}
        </div>

        <div className="flex-1 overflow-y-auto">
          {results.length > 0 ? (
            <div className="space-y-2">
              {results.map((result) => (
                <div
                  key={result.id}
                  className="flex items-center p-3 bg-gray-50 rounded"
                >
                  <div className="mr-3">
                    {result.status === 'pending' && (
                      <Loader2 className="w-5 h-5 text-blue-500 animate-spin" />
                    )}
                    {result.status === 'valid' && (
                      <CheckCircle className="w-5 h-5 text-green-500" />
                    )}
                    {result.status === 'invalid' && (
                      <XCircle className="w-5 h-5 text-red-500" />
                    )}
                    {result.status === 'error' && (
                      <AlertCircle className="w-5 h-5 text-yellow-500" />
                    )}
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="font-medium truncate">{result.title}</div>
                    <div className="text-sm text-gray-500 truncate">
                      {result.url}
                    </div>
                    {(result.statusCode || result.error) && result.status !== 'valid' && (
                      <div className="text-sm text-red-500">
                        {result.statusCode
                          ? `状态码: ${result.statusCode}${result.error?.includes('但页面可能有效') ? ' (但页面可能有效)' : ''}`
                          : result.error}
                      </div>
                    )}
                    {result.status === 'valid' && result.statusCode === undefined && (
                      <div className="text-sm text-green-500 flex items-center">
                        <span>已手动标记为有效</span>
                        <button
                          onClick={() => undoMarkAsValid(result.id)}
                          className="ml-2 text-gray-400 hover:text-gray-600"
                          title="撤销标记"
                        >
                          <Undo2 className="w-3 h-3" />
                        </button>
                      </div>
                    )}
                    {result.status === 'invalid' && result.statusCode === 404 && (
                      <div className="text-xs text-gray-500 mt-1">
                        提示: 404错误可能是网站防爬虫机制导致，您可以手动在浏览器中验证
                      </div>
                    )}
                  </div>
                  {(result.status === 'invalid' || result.status === 'error') && (
                    <div className="ml-2 flex space-x-1">
                      <button
                        onClick={() => window.open(result.url, '_blank')}
                        className="text-blue-500 hover:text-blue-700"
                        title="在浏览器中打开"
                      >
                        <ExternalLink className="w-4 h-4" />
                      </button>
                      <button
                        onClick={() => markAsValid(result.id)}
                        className="text-green-500 hover:text-green-700"
                        title="手动标记为有效"
                      >
                        <CheckCircle className="w-4 h-4" />
                      </button>
                    </div>
                  )}
                </div>
              ))}
            </div>
          ) : (
            <div className="text-center text-gray-500 py-8">
              点击"开始检查"按钮开始验证书签有效性
            </div>
          )}
        </div>
      </div>
    </div>
  )
} 